import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axiosInstance';

const initialState = {
  user: null,
  organization: null,
  token: localStorage.getItem('token'),
  loading: false,
  error: null,
  scormProgress: {},
  unreadCount: 0
};

export const registerOrganization = createAsyncThunk(
  'auth/registerOrganization',
  async (signupData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/auth/register-organization', signupData);
      localStorage.setItem('token', response.data.token);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Organization registration failed');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      // Check if it's FormData or regular JSON
      const isFormData = profileData instanceof FormData;
      
      const config = {
        headers: {
          'Content-Type': isFormData ? 'multipart/form-data' : 'application/json',
        },
      };
      
      // Log what we're sending
      console.log('Updating profile with data:', profileData);
      
      const response = await axiosInstance.put('/auth/profile', profileData, config);
      console.log('Profile update response:', response.data);
      
      // Return the response data with user info
      return response.data;
    } catch (error) {
      console.error('Profile update error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile');
    }
  }
);

export const setUnreadCount = createAsyncThunk(
  'auth/setUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/messages/unread-count');
      return response.data.unreadCount;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get unread count');
    }
  }
);

export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
      console.log('Sending login request with:', { email });
      
      // Direct axios post to the login endpoint
      const response = await axiosInstance.post('/auth/login', { email, password });
      
      // Log successful response
      console.log('Login successful, response:', response.data);
      
      // Store token in localStorage
      localStorage.setItem('token', response.data.token);
      
      // Return the full response data
      return response.data;
    } catch (error) {
      console.error('Login error details:', error);
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/auth/register', userData);
      localStorage.setItem('token', response.data.token);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

export const fetchUserProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/auth/profile');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile');
    }
  }
);

export const updateScormProgress = createAsyncThunk(
  'auth/updateScormProgress',
  async ({ lessonId, status, progress }, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      const response = await axiosInstance.post('/scorm-tracking', {
        lessonId,
        status,
        progress
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return response.data;
    } catch (error) {
      console.error('SCORM progress update error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to update SCORM progress');
    }
  }
);

// Redux Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.organization = null;
      state.token = null;
      state.scormProgress = {};
      state.unreadCount = 0;
      state.loading = false;
      localStorage.clear();
      return state;
    },
    clearError: (state) => {
      state.error = null;
    },
    setScormProgress: (state, action) => {
      const { lessonId, progress } = action.payload;
      state.scormProgress[lessonId] = progress;
    },
    decrementUnreadCount: (state) => {
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    }
  },
  extraReducers: (builder) => {
    builder
      // Organization registration cases
      .addCase(registerOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.admin;
        state.organization = action.payload.organization;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(registerOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        console.log('Login fulfilled:', {
          user: action.payload.user,
          redirectPath: action.payload.redirectPath,
          role: action.payload.user.role
        });
        state.loading = false;
        state.user = {
          ...action.payload.user,
          role: action.payload.user.role // Explicitly ensure role is set
        };
        state.organization = action.payload.organization;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Register cases
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.organization = action.payload.organization;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Profile fetch cases
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = {
          ...state.user,
          ...action.payload,
          role: action.payload.role || state.user?.role, // Preserve role if not in payload
          avatar_url: action.payload.avatar_url // Ensure avatar_url is set
        };
        state.organization = action.payload.organization;
        state.error = null;
        
        // Log updated user with avatar
        console.log('Profile fetch updated user with avatar:', state.user);
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // If unauthorized, clear the token
        if (action.error?.message?.includes('401')) {
          state.token = null;
          localStorage.removeItem('token');
        }
      })

      // Profile update cases
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.user) {
          state.user = {
            ...state.user,
            ...action.payload.user,
            first_name: action.payload.user.first_name,
            last_name: action.payload.user.last_name,
            email: action.payload.user.email,
            avatar_url: action.payload.user.avatar_url, // Ensure avatar URL is updated
            role: action.payload.user.role || state.user.role
          };
        }
        state.error = null;
      
        // Log the updated state
        console.log('Updated user state with avatar:', state.user);
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // SCORM progress cases
      .addCase(updateScormProgress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateScormProgress.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.lessonId) {
          state.scormProgress[action.payload.lessonId] = action.payload.progress;
        }
        state.error = null;
      })
      .addCase(updateScormProgress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Unread count cases
      .addCase(setUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      });
  }
});

export const { logout, clearError, setScormProgress, decrementUnreadCount } = authSlice.actions;

// Selectors
export const selectUser = (state) => state.auth.user;
export const selectOrganization = (state) => state.auth.organization;
export const selectToken = (state) => state.auth.token;
export const selectIsAuthenticated = (state) => !!state.auth.token;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectScormProgress = (state) => state.auth.scormProgress;
export const selectUnreadCount = (state) => state.auth.unreadCount;

export default authSlice.reducer;